import { Container, Box, Tab, CircularProgress, Grid } from '@mui/material'
import { createContext, SyntheticEvent, useState, useEffect, useRef } from 'react'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import PersonalAndContactDetails from '../../../components/PersonalAndContactDetails/PersonalAndContactDetails'
import { AddressAndEmployment } from '../../../components/AddressAndEmployment/AddressAndEmployment'
import { BankDetails } from '../../../components/BankDetails/BankDetails'
import getUserStaticData, { emptyOption, StaticFormData } from '../../../services/ntuc/userService/staticData'
import { SignupRouteParams } from '../SignupFlow/SignupFlow'
import ReferralCode from '../../../components/ReferralCode/ReferralCode'
import { useSignupStore } from '../../../store/signup/UserSignupStore'
import { ActionType } from '../../../store/signup/SignupReducer'
import PersonalDetailsValidator from '../../../services/validation/personal-and-contact-details-form/PersonalDetailsValidator'
import AddressAndEmployementDetailsValidator from '../../../services/validation/address-employment-form/AddressAndEmployementDetailsValidator'
import BankDetailsValidator from '../../../services/validation/bank-details-form/BankDetailsValidator'
import { AppCookies } from '../../../helpers/cookies'
import { useCookies } from 'react-cookie'
import { getWebCampaign } from '../../../services/ntuc/memberService/campaign'

const StaticDataContext = createContext<StaticFormData | undefined>(undefined)
const DEFAULT_DISABLED_STATE = process.env.NODE_ENV !== `development` || false

export enum SignupTabs {
    PERSONAL = `personal`,
    EMPLOYMENT = `employment`,
    BANK = `bank`,
    REFERRAL = `referral`,
}

interface Tab {
    name: string
    isDisabled: boolean
}

function SignupFormPage(props: SignupRouteParams) {
    const { onNavigateTo, routeParams } = props
    const [currentTab, setCurrentTab] = useState<string>(SignupTabs.PERSONAL)
    const [staticData, setStaticData] = useState<StaticFormData | undefined>(undefined)
    const tabNameList: Tab[] = [
        { name: SignupTabs.PERSONAL as string, isDisabled: false },
        { name: SignupTabs.EMPLOYMENT as string, isDisabled: DEFAULT_DISABLED_STATE },
        { name: SignupTabs.BANK as string, isDisabled: DEFAULT_DISABLED_STATE },
        { name: SignupTabs.REFERRAL as string, isDisabled: DEFAULT_DISABLED_STATE },
    ]
    const [tabList, setTabList] = useState(tabNameList)
    const { t } = useTranslation([`common`, `signup`])
    const editTab = routeParams ? routeParams.form.edit : null
    const myRef = useRef<HTMLHeadingElement>(null)
    const [state, dispatch] = useSignupStore()
    const [cookies] = useCookies()

    const isFromReviewPage = routeParams && routeParams.form.edit

    // if (myRef.current != null) {
    //     myRef.current.scrollIntoView()
    // }

    useEffect(() => {
        if (cookies[AppCookies.CAMPAIGN_CODE] && !state.campaign) {
            getWebCampaign(cookies[AppCookies.CAMPAIGN_CODE])
                .then(response => response.data.content)
                .then(campaign => {
                    dispatch({
                        type: ActionType.UPDATE_CAMPAIGN,
                        value: {
                            code: campaign.campaign_code,
                            app: campaign.calling_app,
                            callback: campaign.callback_url,
                        },
                    })
                })

            if (!window.location.href.includes(`&CampaignCode=`)) {
                window.history.pushState({}, ``, `${window.location.href}&CampaignCode=${cookies[AppCookies.CAMPAIGN_CODE]}&utm_campaign=${cookies[AppCookies.CAMPAIGN_CODE]}&utm_source=${cookies[AppCookies.CAMPAIGN_CODE]}&utm_medium=${cookies[AppCookies.CAMPAIGN_CODE]}`)
            }
        }
    }, [])

    useEffect(() => {
        if (isFromReviewPage) {
            enableAllTabs()
        }
    }, [])

    useEffect(() => {
        enableTab(currentTab)
    }, [currentTab])

    // useEffect(() => {
    //     if (editTab) {
    //         setCurrentTab(editTab)
    //     }
    // }, [editTab])

    useEffect(() => {
        getUserStaticData()
            .then(response => response.data.content)
            .then(setStaticData)
            .catch(console.error)
    }, [])

    useEffect(() => {
        reconcileAddressAndEmploymentDropDownDefaults()
        reconcilePersonalAndContactDropDownDefaults()
        reconcileBankDropDownDefaults()

        determineLatestIncompleteTab().then(value => {
            if (editTab) {
                setCurrentTab(editTab)
            } else {
                setCurrentTab(value)
            }
        })
    }, [staticData])

    // useEffect(() => {
    //     determineLatestIncompleteTab().then(value => {
    //         if (value !== SignupTabs.REFERRAL) {
    //             setCurrentTab(value)
    //         }
    //     })
    // }, [currentTab])

    const determineLatestIncompleteTab = async (): Promise<string> => {
        try {
            await PersonalDetailsValidator(state, staticData)

            try {
                await AddressAndEmployementDetailsValidator(state)

                try {
                    await BankDetailsValidator(state)
                    return SignupTabs.REFERRAL
                } catch {
                    return SignupTabs.BANK
                }
            } catch {
                return SignupTabs.EMPLOYMENT
            }
        } catch {
            return SignupTabs.PERSONAL
        }
    }

    const reconcilePersonalAndContactDropDownDefaults = () => {
        if (staticData) {
            const maritalStatus = state.personalDetail.maritalStatus ?
                staticData.marital_status.filter(option => option.code === state.personalDetail.maritalStatus.code)[0] ?? emptyOption() :
                state.personalDetail.maritalStatus

            const race = state.personalDetail.race ?
                staticData.race.filter(option => option.code === state.personalDetail.race.code)[0] ?? emptyOption() :
                state.personalDetail.race

            const educationalStatus = state.personalDetail.educationalStatus ?
                staticData.education_level.filter(option => option.code === state.personalDetail.educationalStatus.code)[0] ?? emptyOption() :
                state.personalDetail.educationalStatus

            const residentialStatus = state.personalDetail.residentialStatus ?
                staticData.residential_status.filter(option => option.code === state.personalDetail.residentialStatus.code)[0] ?? emptyOption() :
                state.personalDetail.residentialStatus

            dispatch({
                type: ActionType.ADD_PERSONAL_DETAILS,
                value: {
                    ...state.personalDetail,
                    maritalStatus,
                    race,
                    educationalStatus,
                    residentialStatus,
                },
            })
        }
    }

    const reconcileAddressAndEmploymentDropDownDefaults = () => {
        if (staticData) {
            if (state.addressAndEmploymentDetail.occupationalGroup && state.addressAndEmploymentDetail.monthlySalary) {
                const occupationalGroup = state.addressAndEmploymentDetail.occupationalGroup ?
                    staticData.occupation_group.filter(option => option.code === state.addressAndEmploymentDetail.occupationalGroup.code)[0] ?? emptyOption() :
                    state.addressAndEmploymentDetail.occupationalGroup

                const monthlySalary = state.addressAndEmploymentDetail.monthlySalary ?
                    staticData.monthly_income.filter(option => option.code === state.addressAndEmploymentDetail.monthlySalary.code)[0] ?? emptyOption() :
                    state.addressAndEmploymentDetail.monthlySalary

                dispatch({
                    type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                    value: {
                        ...state.addressAndEmploymentDetail,
                        occupationalGroup,
                        monthlySalary,
                    },
                })
            }
        }
    }

    const reconcileBankDropDownDefaults = () => {
        if (staticData) {
            if (state.addressAndEmploymentDetail.occupationalGroup && state.addressAndEmploymentDetail.monthlySalary) {
                const bankName = state.bankDetail.bankName ?
                    staticData.bank.filter(option => option.code === state.bankDetail.bankName.code)[0] ?? emptyOption() :
                    state.bankDetail.bankName

                dispatch({
                    type: ActionType.ADD_BANK_DETAILS,
                    value: {
                        ...state.bankDetail,
                        bankName,
                    },
                })
            }
        }
    }

    const handleTabChange = (event: SyntheticEvent, newValue: string) => {
        setCurrentTab(newValue)
        if (myRef.current !== null)
            myRef.current.scrollIntoView()
    }

    const changeTabCallback = (value: string): void => {
        if (!isFromReviewPage) {
            enableTab(value)
        }

        if (tabList.map(element => element.name).includes(value)) {
            setCurrentTab(value)
            if (myRef.current !== null)
                myRef.current.scrollIntoView()
        }
    }

    const tabs = tabList.map((value, key) => {
        return (
            <Tab key={key} wrapped label={t(`tabs.${value.name}.label`, { ns: `signup` })}
                value={value.name}
                data-testid={`signup_form_tab_ ${value.name}`}
                sx={{ borderBottom: `9px solid`, borderColor: `secondary` }}
                disabled={value.isDisabled}
            />
        )
    })

    const enableTab = (value: string): void => {
        setTabList(prev => {
            const index = tabNameList.findIndex(element => element.name === value)
            const tabs = [...prev]
            for (let i = 0; i <= index; i++) {
                tabs[i] = { ...tabs[i], isDisabled: false }
            }
            return tabs
        })
    }

    const enableAllTabs = () => {
        setTabList(tabList.map((tab) => ({
            ...tab,
            isDisabled: false,
        })))
    }

    if (staticData) {
        return (
            <Container id='main-container'>
                <StaticDataContext.Provider value={staticData}>
                    <TabContext value={currentTab}>
                        <Box ref={myRef}>
                            <TabList
                                onChange={handleTabChange}
                                aria-label="Signup form tabs"
                                textColor="primary"
                                variant="fullWidth"
                                TabIndicatorProps={{ className: `ntuc-indicatorSpan` }}
                            >
                                {tabs}
                            </TabList>
                        </Box>
                        <TabPanel value="personal">
                            <PersonalAndContactDetails changeTab={changeTabCallback} onNavigateTo={onNavigateTo} />
                        </TabPanel>
                        <TabPanel value="employment">
                            <AddressAndEmployment changeTab={changeTabCallback} onNavigateTo={onNavigateTo} defaultMonthlyIncomeOption={staticData.monthly_income[0]} />
                        </TabPanel>
                        <TabPanel value="bank">
                            <BankDetails changeTab={changeTabCallback} onNavigateTo={onNavigateTo} />
                        </TabPanel>
                        <TabPanel value="referral">
                            <ReferralCode changeTab={changeTabCallback} onNavigateTo={onNavigateTo} />
                        </TabPanel>
                    </TabContext>
                </StaticDataContext.Provider>
            </Container>
        )
    } else {
        return <LoadingBody />
    }
}

const LoadingBody = () => {
    return (
        <Grid container item justifyContent="center" py={6} xs={12}>
            <CircularProgress />
        </Grid>
    )
}

export default SignupFormPage
export { StaticDataContext }
